"use client";

import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { AppData, Client, CompanyProfile, Draw, DrawInvoice, Job, SowItem } from "@/lib/types";

export type CloudStatus = "offline-demo" | "connecting" | "signed-out" | "portal-signed-out" | "ready" | "portal" | "error";

type DatabaseClient = {
  id: string;
  name: string;
  company: string | null;
  email: string;
  phone: string;
  address: string;
  gc_fee_percent?: number | string | null;
};

type DatabaseJob = {
  id: string;
  client_id: string;
  title: string;
  address: string;
  status: Job["status"];
  reference_number: string | null;
  start_date: string | null;
  target_completion_date: string | null;
  project_manager: string | null;
  customer_name: string | null;
  contract_amount: number | string | null;
  contract_type: string | null;
};

type DatabaseSow = {
  id: string;
  job_id: string;
  title: string;
  description: string;
  contract_amount: number | string;
  approved_budget_changes: number | string | null;
  approved_change_reason: string | null;
};

type DatabaseCompanyProfile = {
  id: "primary";
  legal_name: string;
  display_name: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  logo_url: string;
};

type DatabaseDraw = {
  id: string;
  sow_id: string;
  amount: number | string;
  explanation: string;
  status: Draw["status"];
  estimated_arrival_date: string | null;
  received_at: string | null;
  request_id: string | null;
  request_number: string | null;
  approved_amount: number | string | null;
  withheld_amount: number | string;
  holdback_percent: number | string;
  holdback_reason: string;
  gc_fee_percent?: number | string | null;
  created_at: string;
  draw_photos?: Array<{ object_path: string; display_name: string; signed_url?: string }>;
};

type DatabaseInvoice = {
  request_id: string;
  request_number: string;
  client_id: string;
  job_id: string;
  requested_subtotal: number | string;
  gc_fee_percent?: number | string;
  gc_fee_amount: number | string;
  requested_total: number | string;
  approved_total: number | string;
  withheld_total: number | string;
  holdback_percent: number | string;
  holdback_reason: string;
  status: DrawInvoice["status"];
  remaining_contract_balance: number | string;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
};

declare global {
  interface Window {
    __AQUA_CONFIG__?: {
      supabaseUrl?: string;
      supabasePublishableKey?: string;
      portalFunctionUrl?: string;
    };
  }
}

let client: SupabaseClient | null | undefined;

function value(name: "supabaseUrl" | "supabasePublishableKey" | "portalFunctionUrl") {
  if (typeof document === "undefined") return "";
  const fromWindow = window.__AQUA_CONFIG__?.[name]?.trim();
  if (fromWindow) return fromWindow;
  const root = document.documentElement.dataset;
  if (name === "supabaseUrl") return root.supabaseUrl?.trim() || "";
  if (name === "supabasePublishableKey") return root.supabasePublishableKey?.trim() || "";
  return root.portalFunctionUrl?.trim() || "";
}

export function cloudConfig() {
  const supabaseUrl = value("supabaseUrl");
  const supabasePublishableKey = value("supabasePublishableKey");
  const portalFunctionUrl = value("portalFunctionUrl") || (supabaseUrl ? `${supabaseUrl}/functions/v1/aquadraw-portal` : "");
  return { supabaseUrl, supabasePublishableKey, portalFunctionUrl, configured: Boolean(supabaseUrl && supabasePublishableKey) };
}

export function getCloudClient() {
  if (client !== undefined) return client;
  const config = cloudConfig();
  client = config.configured
    ? createClient(config.supabaseUrl, config.supabasePublishableKey, {
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, storageKey: "aquadraw-owner-session" },
    })
    : null;
  return client;
}

export function resetCloudClientForTests() {
  client = undefined;
}

function mapClient(row: DatabaseClient): Client {
  return { id: row.id, name: row.name, company: row.company || undefined, email: row.email, phone: row.phone, address: row.address, ...(row.gc_fee_percent == null ? {} : { gcFeePercent: Number(row.gc_fee_percent) }) };
}

function mapJob(row: DatabaseJob): Job {
  return {
    id: row.id,
    clientId: row.client_id,
    title: row.title,
    address: row.address,
    status: row.status,
    referenceNumber: row.reference_number || undefined,
    startDate: row.start_date || undefined,
    targetCompletion: row.target_completion_date || undefined,
    projectManager: row.project_manager || undefined,
    customerName: row.customer_name || undefined,
    contractAmount: row.contract_amount == null ? undefined : Number(row.contract_amount),
    contractType: row.contract_type || undefined,
  };
}

function mapSow(row: DatabaseSow): SowItem {
  return { id: row.id, jobId: row.job_id, title: row.title, description: row.description, contractAmount: Number(row.contract_amount), approvedBudgetChanges: Number(row.approved_budget_changes || 0), approvedChangeReason: row.approved_change_reason || "" };
}

function mapCompanyProfile(row?: DatabaseCompanyProfile | null): CompanyProfile | null {
  if (!row) return null;
  return { id: "primary", legalName: row.legal_name, displayName: row.display_name, address: row.address, phone: row.phone, email: row.email, website: row.website, logoUrl: row.logo_url };
}

function mapDraw(row: DatabaseDraw): Draw {
  const photos = row.draw_photos || [];
  return {
    id: row.id,
    sowId: row.sow_id,
    amount: Number(row.amount),
    explanation: row.explanation,
    status: row.status,
    estimatedArrivalDate: row.estimated_arrival_date || undefined,
    receivedAt: row.received_at || undefined,
    requestId: row.request_id || row.id,
    requestNumber: row.request_number || undefined,
    approvedAmount: row.approved_amount == null ? undefined : Number(row.approved_amount),
    withheldAmount: Number(row.withheld_amount || 0),
    holdbackPercent: Number(row.holdback_percent || 0),
    holdbackReason: row.holdback_reason || "",
    ...(row.gc_fee_percent == null ? {} : { gcFeePercent: Number(row.gc_fee_percent) }),
    createdAt: row.created_at,
    mediaNames: photos.map((photo) => photo.display_name),
    photoDataUrls: photos.map((photo) => photo.signed_url).filter((url): url is string => Boolean(url)),
  };
}

function mapInvoice(row: DatabaseInvoice): DrawInvoice {
  return {
    requestId: row.request_id,
    requestNumber: row.request_number,
    clientId: row.client_id,
    jobId: row.job_id,
    requestedSubtotal: Number(row.requested_subtotal),
    gcFeePercent: Number(row.gc_fee_percent || 0),
    gcFeeAmount: Number(row.gc_fee_amount || 0),
    requestedTotal: Number(row.requested_total),
    approvedTotal: Number(row.approved_total),
    withheldTotal: Number(row.withheld_total),
    holdbackPercent: Number(row.holdback_percent),
    holdbackReason: row.holdback_reason || "",
    status: row.status,
    remainingContractBalance: Number(row.remaining_contract_balance),
    approvedAt: row.approved_at || undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function mapSnapshot(payload: unknown): AppData {
  const record = payload && typeof payload === "object" ? payload as Record<string, unknown> : {};
  return {
    companyProfile: mapCompanyProfile(record.companyProfile as DatabaseCompanyProfile | undefined),
    invoices: Array.isArray(record.invoices) ? (record.invoices as DatabaseInvoice[]).map(mapInvoice) : [],
    clients: Array.isArray(record.clients) ? (record.clients as DatabaseClient[]).map(mapClient) : [],
    jobs: Array.isArray(record.jobs) ? (record.jobs as DatabaseJob[]).map(mapJob) : [],
    sowItems: Array.isArray(record.sowItems) ? (record.sowItems as DatabaseSow[]).map(mapSow) : [],
    draws: Array.isArray(record.draws) ? (record.draws as DatabaseDraw[]).map(mapDraw) : [],
  };
}

export async function loadOwnerSnapshot(supabase: SupabaseClient): Promise<AppData> {
  const [companyProfile, clients, jobs, sowItems, draws, invoices, photos] = await Promise.all([
    supabase.from("company_profiles").select("id,legal_name,display_name,address,phone,email,website,logo_url").eq("id", "primary").maybeSingle(),
    supabase.from("clients").select("id,name,company,email,phone,address,gc_fee_percent").order("created_at"),
    supabase.from("jobs").select("id,client_id,title,address,status,reference_number,start_date,target_completion_date,project_manager,customer_name,contract_amount,contract_type").order("created_at"),
    supabase.from("sow_items").select("id,job_id,title,description,contract_amount,approved_budget_changes,approved_change_reason").order("created_at"),
    supabase.from("draws").select("id,sow_id,amount,explanation,status,estimated_arrival_date,received_at,request_id,request_number,approved_amount,withheld_amount,holdback_percent,holdback_reason,gc_fee_percent,created_at").order("created_at", { ascending: false }),
    supabase.from("draw_invoices").select("request_id,request_number,client_id,job_id,requested_subtotal,gc_fee_percent,gc_fee_amount,requested_total,approved_total,withheld_total,holdback_percent,holdback_reason,status,remaining_contract_balance,approved_at,created_at,updated_at").eq("visible_in_archive", true).order("created_at", { ascending: false }),
    supabase.from("draw_photos").select("draw_id,object_path,display_name"),
  ]);
  const error = companyProfile.error || clients.error || jobs.error || sowItems.error || draws.error || invoices.error || photos.error;
  if (error) throw error;
  const photoRows = photos.data || [];
  const photoPaths = photoRows.map((photo) => photo.object_path);
  const signedByPath = new Map<string, string>();
  if (photoPaths.length) {
    const signedBatch = await supabase.storage.from("draw-photos").createSignedUrls(photoPaths, 3600);
    for (const [index, signed] of (signedBatch.data || []).entries()) {
      const path = signed.path || photoPaths[index];
      if (path && signed.signedUrl) signedByPath.set(path, signed.signedUrl);
    }
  }
  const photosByDraw = new Map<string, Array<{ object_path: string; display_name: string; signed_url?: string }>>();
  for (const photo of photoRows) {
    const current = photosByDraw.get(photo.draw_id) || [];
    current.push({ object_path: photo.object_path, display_name: photo.display_name, signed_url: signedByPath.get(photo.object_path) });
    photosByDraw.set(photo.draw_id, current);
  }
  return {
    companyProfile: mapCompanyProfile(companyProfile.data as DatabaseCompanyProfile | null),
    invoices: ((invoices.data || []) as DatabaseInvoice[]).map(mapInvoice),
    clients: (clients.data as DatabaseClient[]).map(mapClient),
    jobs: (jobs.data as DatabaseJob[]).map(mapJob),
    sowItems: (sowItems.data as DatabaseSow[]).map(mapSow),
    draws: (draws.data as DatabaseDraw[]).map((draw) => mapDraw({ ...draw, draw_photos: photosByDraw.get(draw.id) || [] })),
  };
}

export async function callPortal(clientId: string, portalToken: string, body: Record<string, unknown> = { action: "snapshot" }) {
  const config = cloudConfig();
  if (!config.portalFunctionUrl) throw new Error("The customer portal service is not configured.");
  const supabase = getCloudClient();
  const session = supabase ? await supabase.auth.getSession() : null;
  const accessToken = session?.data.session?.access_token || "";
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    apikey: config.supabasePublishableKey,
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const response = await fetch(config.portalFunctionUrl, {
    method: "POST",
    headers,
    body: JSON.stringify({ ...body, clientId, portalToken }),
  });
  const payload = await response.json().catch(() => ({ error: `Portal service returned HTTP ${response.status}.` })) as Record<string, unknown>;
  if (!response.ok) throw new Error(typeof payload.error === "string" ? payload.error : "The customer portal is unavailable.");
  return payload;
}

export function readPortalAccess() {
  if (typeof window === "undefined") return { clientId: "", portalToken: "" };
  const clientStorageKey = "aquadraw:installed-portal-client:v1";
  const tokenStorageKey = "aquadraw:installed-portal-token:v1";
  const query = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const clientId = (hash.get("portalClient") || query.get("portalClient") || "").trim();
  const portalToken = (hash.get("portalToken") || query.get("portalToken") || "").trim();
  if (/^[0-9a-f-]{36}$/i.test(clientId) && /^[A-Za-z0-9_-]{40,256}$/.test(portalToken)) {
    try {
      window.localStorage.setItem(clientStorageKey, clientId);
      window.localStorage.setItem(tokenStorageKey, portalToken);
    } catch { /* Installation still works when storage is restricted. */ }
    return { clientId, portalToken };
  }
  const installedLaunch = query.get("pwa") === "1";
  if (!installedLaunch) return { clientId: "", portalToken: "" };
  try {
    const savedClientId = (window.localStorage.getItem(clientStorageKey) || "").trim();
    const savedToken = (window.localStorage.getItem(tokenStorageKey) || "").trim();
    return /^[0-9a-f-]{36}$/i.test(savedClientId) && /^[A-Za-z0-9_-]{40,256}$/.test(savedToken)
      ? { clientId: savedClientId, portalToken: savedToken }
      : { clientId: "", portalToken: "" };
  } catch { return { clientId: "", portalToken: "" }; }
}

export async function ownerAccessToken() {
  const supabase = getCloudClient();
  if (!supabase) return "";
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token || "";
}
